import { inject, injectable } from 'inversify';
import { makeAutoObservable, reaction } from 'mobx';
import { IOsiAccrualsByAbonentViewModel } from '@shared/types/mobx/widgets/OsiAccrualsByAbonentWidget';
import { Abonent } from '@shared/types/osi/abonents';
import { getOsiAbonents } from '@shared/api/osi/abonents/get';
import { makeFlat } from '@shared/utils/helpers/makeFlat';
import { notifications } from '@shared/utils/notifications';
import logger from 'js-logger';
import moment from 'moment';
import { sumBy } from 'lodash';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { queryClient } from '@shared/api/reactQuery';
import axios from 'axios';
import i18next from 'i18next';
import { getGetApiTransactionsAccuralsByAbonentAndServicesAbonentIdQueryOptions } from '@/shared/api/orval/transactions/transactions';
import { AreaTypeCodes } from '@shared/types/dictionaries';
// Утилита схлопывания дочерних помещений: isChildAreaType — признак паркинга/кладовки,
// mergeAccrualGroups — слияние групп начислений детей в группы родителя
import { isChildAreaType, mergeAccrualGroups } from '@shared/utils/helpers/collapseChildAbonents';

function getUniqName(name: any) {
  if (!name) {
    return `${name}#`;
  }
  return name;
}

function getRows(abonents: any) {
  return abonents.reduce(
    (acc: any, item: any) => [
      ...acc,
      {
        id: item.groupName,
        level: 0,
        groupName: [item.groupName],
        totalAccrual: sumBy(item.services, 'accural'),
        totalFix: sumBy(item.services, 'fix'),
        totalTotal: sumBy(item.services, 'total')
      },
      ...item.services.map((e: any) => ({
        id: `${item.groupName + getUniqName(e.serviceName)}`,
        level: 1,
        groupName: [item.groupName, e.serviceName],
        totalAccrual: e.accural,
        totalFix: e.fix,
        totalTotal: e.total
      }))
    ],
    []
  );
}

@injectable()
export class OsiAccrualsByAbonentViewModel implements IOsiAccrualsByAbonentViewModel {
  abonents: Abonent[] = [];

  dateValue: any = moment().add(-1, 'month');

  isLoading = false;

  rows: any[] = [];

  selectedAbonent: Abonent | null = null;

  // Флаг «Свернуть дочерние помещения»: при true паркинги/кладовки скрыты из выпадающего
  // списка, а их начисления вливаются в начисления родителя.
  // По умолчанию ВКЛЮЧЁН (дочерние помещения свёрнуты, паркинги/кладовки влиты в родителя):
  // class field инициализируется до тела конструктора, поэтому первичный loadAccruals
  // (через reaction после установки selectedAbonent в loadAbonents) уже сливает начисления детей.
  isCollapseChildren = true;

  // --- Кэш сырых начислений для локальной пересборки без сети ---
  // Ключ кэша: «<abonentId>|<месяц>». По смене абонента/периода ключ меняется → кэш невалиден,
  // loadAccruals грузит данные заново. При совпадении ключа переключение флага идёт без сети.
  private _cacheKey: string | null = null;

  // Сырые группы начислений выбранного помещения (родителя) — кэш.
  private _rawParentGroups: any[] = [];

  // Сырые группы начислений дочерних помещений выбранного родителя — кэш.
  // Загружаются один раз вместе с родителем и переиспользуются при переключении флага.
  private _rawChildrenGroups: any[][] = [];

  // Список помещений для Autocomplete: при включённом схлопывании скрываем дочерние
  // помещения (паркинги/кладовки с заданным parentAbonentId)
  get visibleAbonents(): Abonent[] {
    if (!this.isCollapseChildren) {
      return this.abonents;
    }
    return this.abonents.filter(
      (a: any) => !(isChildAreaType(a.areaTypeCode) && a.parentAbonentId != null)
    );
  }

  // OSI-806: признак наличия у ОСИ дочерних помещений (паркинги/кладовки).
  // Источник правды — общий MobX-сервис OsiService (osiInfo.hasChildAbonents), критерий считает бэк
  // (активный абонент с parentAbonentId != null и areaTypeCode ∈ {PARKING, STORAGE}).
  // Нужен UI, чтобы НЕ показывать чекбокс «Свернуть дочерние помещения» в домах, где сворачивать нечего.
  // ВАЖНО: сам флаг isCollapseChildren при скрытом чекбоксе НАМЕРЕННО НЕ сбрасываем (остаётся true) —
  // дочерних помещений нет, поэтому слияние начислений (mergeAccrualGroups) всё равно ничего не меняет.
  get hasChildAbonents(): boolean {
    return this.osiModule.osiInfo?.hasChildAbonents ?? false;
  }

  // Ключ кэша текущего выбора: абонент + месяц периода. Меняется при смене абонента/периода.
  private get _currentCacheKey(): string | null {
    if (this.selectedAbonent?.id == null) return null;
    return `${this.selectedAbonent.id}|${moment(this.dateValue).format('YYYY-MM')}`;
  }

  // Сеттер флага схлопывания.
  // ГЛУБОКИЙ ФИКС РЕГРЕССИИ: переключение флага — ЛОКАЛЬНЫЙ пересчёт из кэша,
  // БЕЗ сетевого запроса и БЕЗ поднятия isLoading (грид не перестраивается с сетевой загрузкой).
  setCollapseChildren = (value: boolean) => {
    this.isCollapseChildren = value;

    // Краевой случай: выбран дочерний абонент (паркинг/кладовка), включили сворачивание —
    // ребёнок исчезает из visibleAbonents → переключаемся на его родителя.
    if (value && this.selectedAbonent && (this.selectedAbonent as any).parentAbonentId != null) {
      const parent = this.abonents.find((a: any) => a.id === (this.selectedAbonent as any).parentAbonentId);
      if (parent) {
        this.selectedAbonent = parent;
        // selectedAbonent сменился → reaction сам вызовет loadAccruals() для родителя.
        // НЕ дёргаем загрузку здесь, чтобы не было ДВОЙНОЙ загрузки.
      }
    }

    // ОСНОВНОЙ КЕЙС: данные текущего выбора (абонент+период) уже в кэше — пересобираем строки
    // ЛОКАЛЬНО (mergeAccrualGroups над кэшем при true, либо чистый родитель при false). Сеть НЕ дёргаем.
    if (this._cacheKey != null && this._cacheKey === this._currentCacheKey) {
      this.rebuildRows();
    }
    // Иначе (кэш-промах после переключения на родителя) — загрузку инициирует reaction по смене selectedAbonent.
  };

  // Пересборка строк грида из кэша сырых начислений (родитель + дети) с учётом флага схлопывания.
  // Без сетевого запроса. Бизнес-логику слияния (mergeAccrualGroups) не меняем — меняем только ИСТОЧНИК.
  private rebuildRows = () => {
    let mergedGroups = this._rawParentGroups;
    if (this.isCollapseChildren && this._rawChildrenGroups.length) {
      mergedGroups = mergeAccrualGroups(this._rawParentGroups, this._rawChildrenGroups);
    }
    this.rows = getRows(mergedGroups);
  };

  private _handleLangChange = () => {
    this.recomputeAbonentFlats();
  };

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);

    this.loadAbonents();

    reaction(
      () => [this.selectedAbonent?.id, this.dateValue],
      async () => {
        await this.loadAccruals();
      }
    );

    i18next.on('languageChanged', this._handleLangChange);
  }

  dispose = () => {
    i18next.off('languageChanged', this._handleLangChange);
  };

  private recomputeAbonentFlats = () => {
    if (!this.abonents?.length) return;
    this.abonents = this.abonents.map((a: any) => {
      // ARENDATOR: flat = flatDisplay (единый номер от backend), от языка не зависит — не пересчитываем
      if (a.areaTypeCode === AreaTypeCodes.ARENDATOR) return a;
      const raw = a.flatRaw ?? a.flat;
      return { ...a, flatRaw: raw, flat: makeFlat({ ...a, flat: raw }) };
    });
  };

  loadAbonents = async (): Promise<void> => {
    try {
      if (!this.osiModule.osiId) throw new Error('osiId is undefined');

      this.isLoading = true;
      const data = await getOsiAbonents(this.osiModule.osiId);

      // Фаза 2: НЕ мутируем flat. flatRaw хранит СЫРОЙ номер помещения (для запросов),
      // flat — отображаемое значение по ГЛАВНОМУ ПРАВИЛУ: арендатор → flatDisplay (единый номер
      // от backend «Аренда(...)»), иначе → makeFlat (префикс типа помещения Неж-/Парк-/Клад-).
      this.abonents = data.map((e: any) => {
        const raw = e.flat;
        const display =
          e.areaTypeCode === AreaTypeCodes.ARENDATOR ? (e.flatDisplay ?? raw) : makeFlat({ ...e, flat: raw });
        return { ...e, flatRaw: raw, flat: display };
      });
      this.selectedAbonent = data?.length ? data[0] : null;
    } catch (e: unknown) {
      logger.error(e);
      if (axios.isAxiosError(e)) {
        notifications.error(e.message);
      }
    } finally {
      this.isLoading = false;
    }
  };

  // Загружает группы начислений одного абонента за текущий период (dateValue).
  // Вызывается и для родителя, и для каждого ребёнка — тем же эндпоинтом с теми же датами.
  private fetchAbonentAccruals = async (abonentId: number) => {
    const queryOptions = getGetApiTransactionsAccuralsByAbonentAndServicesAbonentIdQueryOptions(
      abonentId,
      {
        dateBegin: moment.utc(this.dateValue).startOf('month').local().format('YYYY-MM-DD'),
        dateEnd: moment.utc(this.dateValue).endOf('month').local().format('YYYY-MM-DD')
      },
      { query: { gcTime: 0, staleTime: 0 } }
    );

    const response = await queryClient.fetchQuery(queryOptions);

    if (response.code !== 0) {
      throw new Error(response.message ?? 'Ошибка при запросе начислений');
    }

    // result — массив групп { groupName, services: [{ serviceName, accural, fix, total }] }
    return response.result ?? [];
  };

  loadAccruals = async (): Promise<void> => {
    try {
      if (!this.osiModule.osiId || !this.selectedAbonent?.id) throw new Error('osiId or abonentId is undefined');

      this.isLoading = true;

      const selected = this.selectedAbonent;

      // Группы начислений выбранного помещения (родителя)
      const parentGroups = await this.fetchAbonentAccruals(selected.id);

      // Дети выбранного абонента — паркинги/кладовки, привязанные к нему.
      // Грузим их ВСЕГДА (независимо от флага), чтобы переключение чекбокса пересобиралось
      // локально из кэша без новых сетевых запросов в обе стороны.
      const children = this.abonents.filter(
        (a: any) => a.parentAbonentId === selected.id && isChildAreaType(a.areaTypeCode)
      );
      const childrenGroups = children.length
        ? await Promise.all(children.map((c: any) => this.fetchAbonentAccruals(c.id)))
        : [];

      // Кэшируем сырые данные и фиксируем ключ (абонент+период) для инвалидации по смене выбора
      this._rawParentGroups = parentGroups;
      this._rawChildrenGroups = childrenGroups;
      this._cacheKey = this._currentCacheKey;

      // Строки строим централизованно из кэша с учётом флага (без повторной сети)
      this.rebuildRows();
    } catch (e: unknown) {
      logger.error(e);
      if (axios.isAxiosError(e)) {
        notifications.error(e.message);
      }
    } finally {
      this.isLoading = false;
    }
  };
}
